import { AppLayoutMode, AppSidebarMenuItem } from '@monetize/types/app';
import { ComingSoonIcon } from '@monetize/ui/icons';
import { FC, useEffect, useState } from 'react';
import { Link as ReactDomLink, Location, useLocation } from 'react-router-dom';
import { useAuth } from '../../services/auth0';
import { useNavMenuItems } from '../../hooks/useNavMenuItems';
import {
  MBox,
  MFlex,
  MGrid,
  MIcon,
  MList,
  MListItem,
  MText,
  MTooltip,
} from '../Monetize';

export interface AppMenuProps {
  layoutMode: AppLayoutMode;
}

export const AppMenu = ({ layoutMode }: AppMenuProps) => {
  const { currentTenantUserHasRole } = useAuth();
  const menuItems = useNavMenuItems();

  const isOverlay: boolean = layoutMode === 'overlay';

  const availableItems = menuItems.filter(
    (item) => !item.hidden && currentTenantUserHasRole(item?.roles || []),
  );

  return (
    <MList
      variant="plain"
      role="menu"
      px={isOverlay ? 2 : 3}
      display="flex"
      flexDirection="column"
      gap={1}
    >
      {availableItems.map((item) => (
        <MListItem
          key={item.label}
          style={
            isOverlay
              ? {
                  display: 'flex',
                  justifyContent: 'center',
                }
              : {}
          }
          tabIndex={-1}
        >
          <RenderMenuLink item={item} layoutMode={layoutMode} />
        </MListItem>
      ))}
    </MList>
  );
};

const isRouteActive = (location: Location, item: AppSidebarMenuItem) => {
  if (!item.activeWhen) {
    return location.pathname === item.to;
  }
  return item.activeWhen.some(({ op, route }) => {
    switch (op) {
      case 'startsWith':
        return location.pathname.startsWith(route);
      case 'exact':
        return location.pathname === route;
      default:
        return false;
    }
  });
};

const RenderMenuLink: FC<any> = ({ item, layoutMode }: any) => {
  const location = useLocation();
  const isActive = isRouteActive(location, item);
  const [animationEnded, setAnimationEnded] = useState<boolean>(false);

  const isOverlay = layoutMode === 'overlay';

  useEffect(() => {
    if (isOverlay) {
      setTimeout(() => setAnimationEnded(false), 200);
    } else {
      setTimeout(() => setAnimationEnded(true), 200);
    }
  }, [isOverlay]);

  let color = isActive ? 'fg.link' : 'tGray.darkPurple';
  let iconColor = isActive ? 'fg.link' : 'fg.subtle';

  if (item.comingSoon) {
    color = 'tGray.lightPurple';
    iconColor = 'tGray.lightPurple';
  }

  // Hover shifts color only — never font-weight. Bolding on hover causes a
  // layout jump because the glyph metrics change, so weight stays constant.
  const hoverColor = item.comingSoon ? 'tGray.lightPurple' : 'fg.link';

  return (
    <MTooltip
      label={item.comingSoon && !isOverlay ? 'Coming Soon' : item.label}
      placement="bottom-end"
      isDisabled={!isOverlay && !item.comingSoon}
    >
      {/*
        Single box owns: routing, focus halo, bg fill, and row inset. Keeping
        bg + focus on the same element guarantees the halo and the active
        background share identical bounds — moving them onto separate elements
        is what made the halo bleed past the active fill.
      */}
      <MBox
        asChild
        role="menuitem"
        display={isOverlay ? 'inline-flex' : 'block'}
        textDecoration="none"
        borderRadius="md"
        bg={isActive ? 'tBlue.hover' : 'transparent'}
        _hover={{
          textDecoration: 'none',
          bg: isActive ? 'tBlue.hover' : 'tGray.sidebarDark',
        }}
        _focusVisible={{
          outline: 'none',
          boxShadow: 'focus',
        }}
        data-testid={`menu-${item.label.split(' ').join('-')}`}
      >
        <ReactDomLink to={item.comingSoon ? '#' : item.to} target={item.target}>
          <MGrid
            py={2}
            px={isOverlay ? 2 : 3}
            gridTemplateColumns={!isOverlay ? '0.1fr 1fr' : '1fr'}
            role="group"
            className="group"
            justifyContent="flex-start"
            columnGap={!isOverlay ? 3 : 0}
          >
            <MIcon
              as={item.icon}
              justifyContent="flex-start"
              color={iconColor}
              w={5}
              h={5}
              _groupHover={{ color: hoverColor }}
            />
            {!isOverlay && animationEnded && (
              <MFlex align="center" justify="space-between">
                <MText
                  alignSelf="center"
                  color={color}
                  fontWeight={isActive ? 'semibold' : 'medium'}
                  _groupHover={{ color: hoverColor }}
                >
                  {item.label}
                </MText>
                {item.comingSoon && (
                  <MIcon
                    as={ComingSoonIcon}
                    color={color}
                    display="none"
                    _groupHover={{ color: hoverColor, display: 'block' }}
                  />
                )}
              </MFlex>
            )}
          </MGrid>
        </ReactDomLink>
      </MBox>
    </MTooltip>
  );
};
